/* eslint-disable security/detect-object-injection */
import { ICoreContext } from '@msdyn365-commerce/core';
import { toJS } from 'mobx';
import React, { useEffect, useMemo } from 'react';
import { IOrderDetailDocument, IOrderDetailResponse } from '../../../actions/order-details/get-order-details.action';
import getTdsDocumentAction, {
    GetTDSDocumentDownloadInput,
    IGetTDSDocumentItem
} from '../../../actions/order-details/get-tds-document.action';
import Label from '../../../components/label';
import {
    NO_DATA_FALLBACK,
    ORDER_DETAILS_SDS_DOCUMENT_TYPE,
    ORDER_DETAILS_TDS_DOCUMENT_TYPE,
    ORDER_DOCUMENTS_SECTION
} from '../../../configuration';
import { ILocalizationState } from '../../../states/localization-state';
import { dateLocaleFormatter, toLocalDate } from '../../../utilities';
import DocumentDownloadItem from '../../../utilities/document-download-item';
import { IOrderDetailsResources } from '../order-details.props.autogenerated';
import { DataSheetRow } from './data-sheet-row';

export type IGetTDSDocumentItemExtended = IGetTDSDocumentItem & {
    Name: string;
    Reference: string;
    FileSize: string;
    Date: string;
    Language?: string;
    OrderNumber: string;
    Type: string;
};

export default function DocumentsTable({
    context,
    orderDetails,
    resources,
    localizationState
}: {
    context: ICoreContext;
    orderDetails: IOrderDetailResponse;
    resources: IOrderDetailsResources;
    localizationState: ILocalizationState;
}) {
    useEffect(() => {
        const scrollIntoView = () => {
            const hash = window.location.hash;

            if (hash) {
                const section = document.getElementById(hash.substring(1));
                if (section) {
                    section.scrollIntoView({ behavior: 'smooth' });
                }
            }
        };
        scrollIntoView();
    }, []);

    const [tdsDocumentDownload, setTdsDocumentDownload] = React.useState<{ values: IGetTDSDocumentItem[] } | null>(null);
    const [isLoadingTds, setIsLoadingTds] = React.useState<boolean>(true);

    const displayProductDataByTds = useMemo<{
        number: { [requestOrProductNumber: string]: string };
        name: { [requestOrProductNumber: string]: string };
    }>(() => {
        const productNumberMap: { [requestOrProductNumber: string]: string } = {};
        const productNameMap: { [requestOrProductNumber: string]: string } = {};
        const orderLines = orderDetails?.OrderDetail?.OrderDetailLines ?? [];

        orderLines.forEach(line => {
            const displayProductNumber = line.ProductNumber?.trim?.();
            const requestProductNumber = line.UnpackedProductNumber?.trim?.() || displayProductNumber;
            const displayProductName = line.ProductName?.trim?.();

            if (!displayProductNumber || !displayProductName) {
                return;
            }

            productNumberMap[displayProductNumber] = displayProductNumber;

            if (requestProductNumber) {
                productNumberMap[requestProductNumber] = displayProductNumber;
                productNameMap[requestProductNumber] = displayProductName;
            }

            if (displayProductNumber) {
                productNameMap[displayProductNumber] = displayProductName;
            }
        });

        return { number: productNumberMap, name: productNameMap };
    }, [orderDetails?.OrderDetail?.OrderDetailLines]);
    const normalizedTdsDocuments: IGetTDSDocumentItemExtended[] =
        tdsDocumentDownload?.values.map((doc: IGetTDSDocumentItem) => {
            const referenceProductName = displayProductDataByTds.name[doc.ProductId] || doc.ProductId;

            return {
                Name: `${resources.orderDetailsTdsName}`,
                Reference: referenceProductName ?? '',
                FileSize: '',
                FileName: doc.FileName ?? '',
                RecordCreatedDate: doc.RecordCreatedDate,
                Date: String(doc.RecordCreatedDate ?? ''),
                Language: doc.Language?.split('-')[0]?.toUpperCase(),
                DocumentId: doc.DocumentId,
                OrderNumber: orderDetails?.OrderDetail?.OrderNumber ?? '',
                Type: ORDER_DETAILS_TDS_DOCUMENT_TYPE,
                ExtensionProperties: doc.ExtensionProperties ?? [],
                ProductId: doc.ProductId,
                DownstreamSystem: doc.DownstreamSystem,
                Url: doc.Url,
                Summary: doc.Summary
            };
        }) ?? [];

    const { groupedSecurityDataSheetItems, documentItems } = useMemo(() => {
        const securityDataSheetGroups: { [reference: string]: IOrderDetailDocument[] } = {};
        const documents: IOrderDetailDocument[] = [];

        if (orderDetails.Documents) {
            orderDetails.Documents.map((document: IOrderDetailDocument): void => {
                if (document.Type === ORDER_DETAILS_SDS_DOCUMENT_TYPE) {
                    const reference = document.Reference;
                    if (!securityDataSheetGroups[reference]) {
                        securityDataSheetGroups[reference] = [];
                    }
                    securityDataSheetGroups[reference].push(document);
                } else {
                    documents.push(document);
                }
            });
        }

        return { groupedSecurityDataSheetItems: securityDataSheetGroups, documentItems: documents };
    }, [orderDetails.Documents]);

    const groupedDocuments = useMemo(() => {
        const mergedGroups: { [reference: string]: IOrderDetailDocument[] } = { ...groupedSecurityDataSheetItems };

        normalizedTdsDocuments.forEach(doc => {
            const reference = doc.Reference;
            if (!mergedGroups[reference]) {
                mergedGroups[reference] = [];
            }
            mergedGroups[reference].push(doc);
        });

        return mergedGroups;
    }, [groupedSecurityDataSheetItems, normalizedTdsDocuments]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setIsLoadingTds(true);

                const orderLines = orderDetails?.OrderDetail?.OrderDetailLines ?? [];

                const tdsFetchTargets = Array.from(
                    orderLines
                        .reduce((targets, line) => {
                            const displayProductNumber = line.ProductNumber?.trim?.();
                            const requestProductNumber = line.UnpackedProductNumber?.trim?.() || displayProductNumber;

                            if (!displayProductNumber || !requestProductNumber) {
                                return targets;
                            }

                            targets.set(requestProductNumber, {
                                requestProductNumber,
                                displayProductNumber
                            });

                            return targets;
                        }, new Map<string, { requestProductNumber: string; displayProductNumber: string }>())
                        .values()
                );

                if (tdsFetchTargets.length === 0) {
                    setTdsDocumentDownload(null);
                    return;
                }

                const division = orderDetails?.OrderDetail?.DivisionText;

                // Fetch TDS documents for each product number
                const fetchTdsDocuments = tdsFetchTargets.map(
                    async ({ requestProductNumber }): Promise<IGetTDSDocumentItem[]> => {
                        const input = new GetTDSDocumentDownloadInput(requestProductNumber, division);
                        const tdsDocumentResult = await getTdsDocumentAction(input, context.actionContext);
                        return toJS(tdsDocumentResult?.value ?? []);
                    }
                );

                const tdsDocumentResults = await Promise.all(fetchTdsDocuments);

                const dataResult: IGetTDSDocumentItem[] = tdsDocumentResults.flat();

                // Remove duplicates based on DocumentId
                const uniqueDataResult = dataResult.filter((doc, index, allDocs) => {
                    const documentId = doc?.DocumentId;
                    if (!documentId) {
                        return true;
                    }
                    return index === allDocs.findIndex(candidate => candidate?.DocumentId === documentId);
                });

                setTdsDocumentDownload({
                    values: uniqueDataResult
                });
            } catch (error) {
                console.error('Error loading TDS documents', error);
            } finally {
                setIsLoadingTds(false);
            }
        };

        void fetchData();
    }, [context.actionContext, orderDetails]);

    const dataSheetGroupKeys = Object.keys(groupedDocuments);
    const hasDocuments = dataSheetGroupKeys.length > 0 || documentItems.length > 0;

    return (
        <>
            <h3 id={ORDER_DOCUMENTS_SECTION} className='ms-order-details__section-heading'>
                {resources.orderDetailsDocumentsTableHeading}
            </h3>
            {hasDocuments ? (
                <div className='table-responsive'>
                    <table className='table'>
                        <thead>
                            <tr>
                                <th>{resources.orderDetailsDocumentsTabeleDocumentName}</th>
                                <th>{resources.orderDetailsDocumentsTableDate}</th>
                                <th>{resources.orderDetailsDocumentsTableLanguage}</th>
                                <th>{resources.orderDetailsDocumentsTableReference}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoadingTds && (
                                <tr>
                                    <td colSpan={4}>
                                        <Label className='ms-order-details__documents-loading' label={'loading'} />
                                    </td>
                                </tr>
                            )}
                            {dataSheetGroupKeys.map(reference => (
                                <DataSheetRow
                                    key={reference}
                                    context={context}
                                    resources={resources}
                                    localizationState={localizationState}
                                    dataSheetItems={groupedDocuments[reference]}
                                />
                            ))}

                            {documentItems.map((documentItem, index) => (
                                <tr key={index}>
                                    <td>
                                        <DocumentDownloadItem context={context} resources={resources} documentItem={documentItem} />
                                    </td>
                                    <td>
                                        {documentItem.Date
                                            ? dateLocaleFormatter(localizationState).formatDate(toLocalDate(documentItem.Date))
                                            : NO_DATA_FALLBACK}{' '}
                                    </td>
                                    <td>{documentItem.Language ?? NO_DATA_FALLBACK}</td>
                                    <td>{documentItem.Reference ?? NO_DATA_FALLBACK}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <p className='ms-order-details__documents-placeholder'>{resources.orderDetailsDocumentsEmpty}</p>
            )}
        </>
    );
}
